/**
 * Shared home for ad-hoc *action* icons — glyphs chosen per call site for a generic
 * UI action (settings, edit, overflow menu, visibility toggle, file/upload) rather
 * than for a specific domain entity. Entity-keyed maps (e.g. `SOURCE_ICONS` in
 * `wizard/steps/SourceSelection/SourceSelection.tsx`, `WIZARD_STEP_ICONS` in
 * `wizard/WizardContainer/WizardContainer.types.ts`) stay where they are — this
 * module is only for the generic actions that had no shared name at all.
 *
 * Deliberately NOT a glyph-unification exercise: `file`, `fileUpload`, and `upload`
 * point at three different icons because they render in different semantic contexts
 * (a static "this is a file" indicator vs. a file-specific upload affordance vs. a
 * generic upload action). Repoint call sites to these names without changing which
 * icon renders. See docs/design-audit.md §5 / §7 hotspot #5.
 */
import type { SvgIconComponent } from "@mui/icons-material";
import {
	Settings,
	Edit,
	MoreVert,
	Visibility,
	VisibilityOff,
	UploadFile,
	InsertDriveFileOutlined,
	Upload,
} from "@mui/icons-material";

export const ACTION_ICONS = {
	/** Settings / configuration entry points (nav links, admin tools). */
	settings: Settings,
	/** Edit-in-place / open-for-editing actions. */
	edit: Edit,
	/** Overflow ("kebab") menu triggers. */
	overflowMenu: MoreVert,
	/** "Shown" state of a show/hide (map layer, list item) visibility toggle. */
	visibilityOn: Visibility,
	/** "Hidden" state of a show/hide (map layer, list item) visibility toggle. */
	visibilityOff: VisibilityOff,
	/** Upload-a-file affordance (file glyph + arrow) — file-specific upload actions. */
	fileUpload: UploadFile,
	/** Static "this is a file" indicator — source-file labels, file chips, empty states. */
	file: InsertDriveFileOutlined,
	/** Generic upload action (plain arrow) — not file-specific. */
	upload: Upload,
} satisfies Record<string, SvgIconComponent>;
